import { Injectable, Inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { NavigationService } from './navigation.service';
import { DebugLoggerService } from './core/debug-logger.service';

@Injectable()
export class RoleGuard implements CanActivate {
  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly navigationService: NavigationService,
    private readonly logger: DebugLoggerService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    const roles = (route.data?.roles as Array<string>) ?? [];
    const url = state.url;

    this.logger.info('[RoleGuard] Checking access:', { url, requiredRoles: roles });

    // Wait for authentication to complete before checking permissions
    return from(this.oktaAuth.isAuthenticated()).pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          this.logger.info('[RoleGuard] User not authenticated');
          return false;
        }

        const token = this.oktaAuth.getAccessToken();
        if (!token) {
          this.logger.info('[RoleGuard] No access token available');
          return false;
        }

        // Now safely check permissions
        for (const role of roles) {
          const hasPermission = this.navigationService.checkWasPerm(role);
          this.logger.info('[RoleGuard] Role check:', { role, hasPermission });

          if (!hasPermission) {
            this.logger.info('[RoleGuard] Access DENIED:', { url, missingRole: role });
            return false;
          }
        }

        this.logger.info('[RoleGuard] Access GRANTED:', { url, roles });
        return true;
      })
    );
  }
}
